import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import StatCard from '../../components/common/StatCard';
import DonutChart from '../../components/common/DonutChart';
import FollowUpsTable from '../../components/common/FollowUpsTable';
import UpcomingReminders from '../../components/common/UpcomingReminders';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { Users, Flame, Trophy, CalendarClock } from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      if (!user) return;
      try {
        const response = await api(`/dashboard/stats`);
        if (response.ok) {
          const stats = await response.json();
          setData(stats);
        }
      } catch (err) {
        console.error("Dashboard error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [user]);

  if (loading) return <MainLayout title="Dashboard"><div style={{ padding: '40px', textAlign: 'center' }}>Loading Dashboard...</div></MainLayout>;
  if (!data) return <MainLayout title="Dashboard"><div style={{ padding: '40px', textAlign: 'center' }}>Error loading data</div></MainLayout>;

  const donutData = data.statusBreakdown.map(sb => ({
    name: sb._id || 'New',
    value: sb.count
  }));
  
  const upcomingReminders = data.upcomingReminders || [];

  return (
    <MainLayout title="Dashboard">
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--color-text-primary)', marginBottom: '4px' }}>
            Good morning, {user.name.split(' ')[0]} 👋
          </h1>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>
            Your personal lead performance for {new Date().toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '24px' }}>
        <StatCard 
          label="My Leads" 
          value={data.stats.totalLeads} 
          icon={Users}
          gradient="var(--gradient-blue)"
        />
        <StatCard 
          label="Hot Leads" 
          value={data.stats.hotLeads} 
          icon={Flame}
          iconColor="var(--chart-orange)"
          color="var(--chart-orange)"
          gradient="var(--gradient-orange)"
        />
        <StatCard 
          label="Closed Won" 
          value={data.stats.closedWon} 
          icon={Trophy}
          iconColor="var(--chart-green)"
          color="var(--chart-green)"
          gradient="var(--gradient-green)"
        />
        <StatCard 
          label="Follow-ups Due Today" 
          value={data.todayFollowups ? data.todayFollowups.length : 0} 
          icon={CalendarClock}
          iconColor="var(--chart-purple)"
          color="var(--chart-purple)"
          gradient="var(--gradient-purple)"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        <div className="card" style={{ 
          background: 'var(--color-background-primary)', 
          padding: '24px', 
          borderRadius: 'var(--border-radius-xl)',
          border: '1px solid var(--color-border-tertiary)',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '20px' }}>My Lead Status Distribution</h3>
          <DonutChart data={donutData} />
        </div>
        
        <FollowUpsTable 
          followups={data.todayFollowups} 
          overdueCount={data.overdueCount} 
          onViewAll={() => navigate(`/executive/leads`)}
          onNavigate={(path) => navigate(path)}
        />
        
        <UpcomingReminders reminders={upcomingReminders} />
      </div>
    </MainLayout>
  );
};

export default Dashboard;
